<template>
    <Panel no-padding>
        <!-- Generating badge (height-animated to prevent layout jumps) -->
        <div
            class="grid transition-all duration-300 ease-out"
            :class="showBeautyShotsBadge ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'"
        >
            <div class="overflow-hidden">
                <div class="flex items-center gap-2 border-b border-border px-4 py-3 text-sm text-primary">
                    <svg class="size-4 shrink-0 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>{{ $t('pages.product_detail_page.ai_upload.beauty_shots_generating', { count: pendingBeautyShotCount }) }}</span>
                </div>
            </div>
        </div>

        <!-- Dynamic accordion blocks -->
        <template v-for="(config, index) in panelBlockConfigurations" :key="config.key">
            <div
                v-if="config.visible"
                class="px-4 py-3"
                :class="{ 'border-t border-border/50': panelBlockConfigurations.slice(0, index).some(c => c.visible) }"
            >
                <component :is="config.component" v-bind="config.props" />
            </div>

            <!--
                Host-provided extra beneath the photo-selection block (the desktop generate button).
                Tied to the primed state rather than to the row, which now also shows after a result
                is in — where the button below is the order button and belongs in the general panel.
            -->
            <div
                v-if="config.key === 'photo-selection' && config.visible && showPhotoSelectionBlock && $slots['after-photo-selection']"
                class="px-4 pb-3"
            >
                <slot name="after-photo-selection" />
            </div>
        </template>

        <!-- Limit messages -->
        <div v-if="showUserDailyLimitMessage" class="border-t border-border/50 px-4 py-3">
            <p class="text-sm text-gray-500">
                {{ userLimitMessage }}
            </p>
        </div>

        <div v-if="showThresholdExceededMessage" class="border-t border-border/50 px-4 py-3">
            <p class="text-sm text-gray-500">
                {{ $t('pages.product_detail_page.ai_upload.regenerate.threshold_exceeded') }}
            </p>
        </div>
    </Panel>
</template>

<script setup lang="ts">
import Panel from '@/components/panels/Panel.vue';
import { useAiCreditsRemaining } from '@/composables/ai-upload/useAiCreditsRemaining';
import { useAiPanelState } from '@/composables/ai-upload/useAiPanelState';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * The AI panel content, shared by the desktop/tablet column and the phone bottom sheet. Both used
 * to carry their own copy of this markup, which is how the two surfaces drifted apart.
 */
const {
    showBeautyShotsBadge,
    pendingBeautyShotCount,
    panelBlockConfigurations,
    showPhotoSelectionBlock,
    showUserDailyLimitMessage,
    showThresholdExceededMessage,
} = useAiPanelState();

const { hoursUntilNextCredit } = useAiCreditsRemaining();
const { t } = useI18n();

/**
 * Spells out the wait rather than saying "later". The allowance window rolls, so the exact hour
 * the next credit returns is knowable — see hoursUntilNextUserCredit(). Falls back to the vaguer
 * copy when the server could not work it out.
 */
const userLimitMessage = computed(() =>
    hoursUntilNextCredit.value === undefined
        ? t('pages.product_detail_page.ai_upload.user_limit_reached_unknown_wait')
        : t('pages.product_detail_page.ai_upload.user_daily_limit_reached', { count: hoursUntilNextCredit.value }),
);
</script>
